import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../styles/design-system.js';
import { iconStyles } from '../styles/icon-styles.js';

export class LiveCallsMonitor extends LitElement {
  static properties = {
    calls: { type: Array },
    _selectedCallId: { state: true },
    _filter: { state: true },
    _searchQuery: { state: true }
  };

  static styles = [
    designSystem,
    componentStyles,
    css`
      :host {
        display: block;
        height: 100%;
      }

      /* ─── Split Layout ─── */
      .monitor-layout {
        display: grid;
        grid-template-columns: 360px 1fr;
        height: calc(100vh - var(--topbar-height) - var(--space-12));
        gap: 0;
        background: transparent;
        border-radius: var(--radius-xl);
        overflow: hidden;
        border: 1px solid var(--color-border-primary);
        box-shadow: var(--shadow-md);
      }

      /* ─── Left Panel ─── */
      .left-panel {
        display: flex;
        flex-direction: column;
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-right: 1px solid rgba(255, 255, 255, 0.35);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        position: relative;
        overflow: hidden;
      }

      .left-header {
        padding: var(--space-6);
        border-bottom: 1px solid var(--color-border-primary);
        flex-shrink: 0;
      }

      .left-title {
        font-family: var(--font-display);
        font-size: var(--text-2xl);
        font-weight: var(--font-black);
        color: var(--color-text-primary);
        letter-spacing: var(--tracking-tight);
        margin-bottom: var(--space-4);
      }

      /* ─── Filter Tabs ─── */
      .filter-tabs {
        display: flex;
        gap: var(--space-1);
        margin-bottom: var(--space-4);
      }

      .filter-tab {
        flex: 1;
        padding: var(--space-2) var(--space-2);
        background: transparent;
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-md);
        color: var(--color-text-tertiary);
        font-family: var(--font-body);
        font-size: var(--text-xs);
        font-weight: var(--font-medium);
        cursor: pointer;
        transition: all var(--transition-fast);
        text-align: center;
        white-space: nowrap;
      }

      .filter-tab:hover {
        color: var(--color-text-secondary);
        border-color: var(--color-border-secondary);
      }

      .filter-tab.active {
        background: var(--color-primary);
        border-color: var(--color-primary);
        color: var(--color-text-inverse);
        font-weight: var(--font-semibold);
      }

      .filter-tab .tab-count {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 18px;
        height: 18px;
        padding: 0 4px;
        border-radius: var(--radius-full);
        font-size: 10px;
        font-weight: var(--font-bold);
        margin-left: 4px;
      }

      .filter-tab.active .tab-count {
        background: rgba(255, 255, 255, 0.25);
        color: var(--color-text-inverse);
      }

      .filter-tab:not(.active) .tab-count {
        background: var(--color-bg-tertiary);
        color: var(--color-text-tertiary);
      }

      /* ─── Search ─── */
      .search-box {
        position: relative;
      }

      .search-box input {
        width: 100%;
        padding: var(--space-2) var(--space-3) var(--space-2) var(--space-10);
        background: var(--color-bg-primary);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-md);
        color: var(--color-text-primary);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        transition: all var(--transition-fast);
        box-sizing: border-box;
      }

      .search-box input:focus {
        outline: none;
        border-color: var(--color-primary);
        box-shadow: var(--shadow-glow-primary);
      }

      .search-box input::placeholder {
        color: var(--color-text-tertiary);
      }

      .search-icon {
        position: absolute;
        left: var(--space-3);
        top: 50%;
        transform: translateY(-50%);
        color: var(--color-text-tertiary);
        font-size: var(--text-sm);
        pointer-events: none;
      }

      /* ─── Call Cards List ─── */
      .call-list {
        flex: 1;
        overflow-y: auto;
        padding: var(--space-3);
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }

      .call-card {
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-left: 3px solid transparent;
        border-radius: var(--radius-md);
        padding: var(--space-4);
        cursor: pointer;
        transition: all var(--transition-fast);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        position: relative;
        overflow: hidden;
      }

      .call-card:hover {
        border-color: var(--color-border-secondary);
        border-left-color: var(--color-border-secondary);
        background: var(--color-bg-hover);
      }

      .call-card.selected {
        border-color: var(--color-primary);
        border-left-color: var(--color-primary);
        background: var(--color-primary-light);
        box-shadow: var(--shadow-glow-primary);
      }

      .call-card-top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-2);
      }

      .call-card-phone {
        font-family: var(--font-mono);
        font-size: var(--text-sm);
        font-weight: var(--font-semibold);
        color: var(--color-text-primary);
      }

      .call-card-badge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 2px 8px;
        border-radius: var(--radius-full);
        font-family: var(--font-body);
        font-size: 10px;
        font-weight: var(--font-semibold);
      }

      .call-card-badge.active {
        background: var(--color-success-bg);
        color: var(--color-success-text);
      }

      .call-card-badge.active .badge-dot {
        width: 6px;
        height: 6px;
        border-radius: var(--radius-full);
        background: var(--color-success);
        animation: softPulse 2.5s ease-in-out infinite;
      }

      .call-card-badge.queued {
        background: var(--color-warning-bg);
        color: var(--color-warning-text);
      }

      .call-card-badge.queued .badge-dot {
        width: 6px;
        height: 6px;
        border-radius: var(--radius-full);
        background: var(--color-warning);
      }

      .call-card-badge.hold {
        background: var(--color-info-bg);
        color: var(--color-info-text);
      }

      .call-card-badge.hold .badge-dot {
        width: 6px;
        height: 6px;
        border-radius: var(--radius-full);
        background: var(--color-info);
      }

      .call-card-meta {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
      }

      .call-card-meta-item {
        display: flex;
        align-items: center;
        gap: var(--space-1);
      }

      .call-card-meta-item i {
        font-size: 11px;
      }

      .call-card-duration {
        font-family: var(--font-mono);
        font-weight: var(--font-medium);
        color: var(--color-text-secondary);
      }

      .call-card-sentiment {
        width: 8px;
        height: 8px;
        border-radius: var(--radius-full);
        flex-shrink: 0;
        margin-left: auto;
      }

      .call-card-sentiment.positive { background: var(--color-success); }
      .call-card-sentiment.neutral { background: var(--color-warning); }
      .call-card-sentiment.negative { background: var(--color-error); }

      /* ─── Right Panel ─── */
      .right-panel {
        display: flex;
        flex-direction: column;
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        border: 1px solid rgba(255, 255, 255, 0.35);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        position: relative;
        overflow: hidden;
      }

      /* ─── Detail Header ─── */
      .detail-header {
        padding: var(--space-5) var(--space-6);
        border-bottom: 1px solid var(--color-border-primary);
        flex-shrink: 0;
      }

      .detail-header-top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-3);
      }

      .detail-phone {
        font-family: var(--font-display);
        font-size: var(--text-xl);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
        letter-spacing: var(--tracking-tight);
      }

      .detail-live-badge {
        display: inline-flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-1) var(--space-3);
        background: var(--color-success-bg);
        border-radius: var(--radius-full);
        font-family: var(--font-body);
        font-size: var(--text-xs);
        font-weight: var(--font-semibold);
        color: var(--color-success-text);
      }

      .detail-live-dot {
        width: 7px;
        height: 7px;
        border-radius: var(--radius-full);
        background: var(--color-success);
        animation: softPulse 2.5s ease-in-out infinite;
      }

      .detail-meta {
        display: flex;
        align-items: center;
        gap: var(--space-6);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
      }

      .detail-meta-item {
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }

      .detail-meta-item i {
        font-size: var(--text-sm);
        width: 16px;
        text-align: center;
      }

      .detail-meta-value {
        color: var(--color-text-secondary);
        font-weight: var(--font-medium);
      }

      .detail-meta-value.mono {
        font-family: var(--font-mono);
        font-size: var(--text-xs);
      }

      /* ─── Transcript ─── */
      .transcript-area {
        flex: 1;
        overflow-y: auto;
        padding: var(--space-5) var(--space-6);
        display: flex;
        flex-direction: column;
        gap: var(--space-4);
      }

      .transcript-message {
        display: flex;
        flex-direction: column;
        max-width: 75%;
        animation: slideUp var(--duration-base) var(--ease-out) both;
      }

      .transcript-message.ai {
        align-self: flex-start;
      }

      .transcript-message.caller {
        align-self: flex-end;
      }

      .transcript-bubble {
        padding: var(--space-3) var(--space-4);
        border-radius: var(--radius-lg);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        line-height: var(--leading-relaxed);
        position: relative;
        box-shadow: var(--shadow-xs);
      }

      .transcript-message.ai .transcript-bubble {
        background: var(--color-accent-subtle);
        color: var(--color-text-primary);
        border-bottom-left-radius: var(--radius-sm);
      }

      .transcript-message.caller .transcript-bubble {
        background: var(--color-bg-tertiary);
        color: var(--color-text-primary);
        border-bottom-right-radius: var(--radius-sm);
      }

      .transcript-label {
        font-family: var(--font-body);
        font-size: var(--text-xs);
        font-weight: var(--font-semibold);
        color: var(--color-text-tertiary);
        margin-bottom: var(--space-1);
        text-transform: uppercase;
        letter-spacing: var(--tracking-wider);
      }

      .transcript-message.ai .transcript-label {
        color: var(--color-accent);
      }

      .transcript-time {
        font-family: var(--font-mono);
        font-size: 10px;
        color: var(--color-text-tertiary);
        margin-top: var(--space-1);
      }

      .transcript-message.ai .transcript-time {
        text-align: left;
      }

      .transcript-message.caller .transcript-time {
        text-align: right;
      }

      /* ─── Actions Bar ─── */
      .actions-bar {
        padding: var(--space-4) var(--space-6);
        border-top: 1px solid rgba(255, 255, 255, 0.35);
        display: flex;
        align-items: center;
        gap: var(--space-3);
        flex-shrink: 0;
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        border: 1px solid rgba(255, 255, 255, 0.35);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        position: relative;
        overflow: hidden;
      }

      .action-btn {
        display: inline-flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-2) var(--space-4);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-md);
        background: var(--color-bg-secondary);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: var(--color-text-secondary);
        cursor: pointer;
        transition: all var(--transition-fast);
        white-space: nowrap;
      }

      .action-btn:hover {
        background: var(--color-bg-tertiary);
        color: var(--color-text-primary);
        border-color: var(--color-border-secondary);
      }

      .action-btn.primary {
        background: var(--color-primary);
        border-color: var(--color-primary);
        color: var(--color-text-inverse);
      }

      .action-btn.primary:hover {
        background: var(--color-primary-hover);
        transform: translateY(-1px);
        box-shadow: var(--shadow-md);
      }

      .action-btn.danger {
        color: var(--color-error);
      }

      .action-btn.danger:hover {
        background: var(--color-error-light);
        border-color: var(--color-error);
      }

      .action-btn i {
        font-size: var(--text-sm);
      }

      .actions-spacer {
        flex: 1;
      }

      /* ─── Empty State ─── */
      .empty-panel {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        padding: var(--space-8);
        text-align: center;
      }

      .empty-icon {
        width: 72px;
        height: 72px;
        border-radius: var(--radius-xl);
        background: var(--color-bg-tertiary);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.75rem;
        color: var(--color-text-disabled);
        margin-bottom: var(--space-5);
      }

      .empty-title {
        font-family: var(--font-display);
        font-size: var(--text-lg);
        font-weight: var(--font-semibold);
        color: var(--color-text-secondary);
        margin-bottom: var(--space-2);
      }

      .empty-desc {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
        max-width: 280px;
        line-height: var(--leading-relaxed);
      }

      /* ─── Responsive ─── */
      @media (max-width: 1024px) {
        .monitor-layout {
          grid-template-columns: 1fr;
          height: auto;
        }

        .left-panel {
          border-right: none;
          border-bottom: 1px solid var(--color-border-primary);
          max-height: 400px;
        }

        .right-panel {
          min-height: 500px;
        }
      }

      @media (max-width: 640px) {
        .left-panel {
          max-height: 320px;
        }

        .filter-tabs {
          flex-wrap: wrap;
        }

        .actions-bar {
          flex-wrap: wrap;
        }
      }
    `
  ];

  constructor() {
    super();
    this._selectedCallId = null;
    this._filter = 'all';
    this._searchQuery = '';
    this._timerInterval = null;

    // Mock call data
    this.calls = [
      {
        id: 'call-001',
        phone: '(555) 012-3456',
        agent: 'AI Agent Luna',
        status: 'active',
        duration: 187,
        startedAt: new Date(Date.now() - 187000).toISOString(),
        callSid: 'CA9f8e7d6c5b4a3210',
        sentiment: 'positive',
        transcript: [
          { speaker: 'ai', text: 'Thank you for calling Dewberry Resort. How can I help you today?', time: '0:00' },
          { speaker: 'caller', text: 'Hi, I would like to book a room for next weekend. Do you have anything available?', time: '0:08' },
          { speaker: 'ai', text: 'I\'d be happy to help you with a reservation! Let me check our availability for next weekend. Are you looking for a standard room or a suite?', time: '0:14' },
          { speaker: 'caller', text: 'A suite would be great if you have one. Preferably with an ocean view.', time: '0:24' },
          { speaker: 'ai', text: 'Wonderful choice! I can see we have two ocean-view suites available for next Friday through Sunday. The Coastal Suite is $289 per night and the Premier Suite is $429 per night. Would you like details on either?', time: '0:30' },
          { speaker: 'caller', text: 'Tell me more about the Coastal Suite.', time: '0:48' }
        ]
      },
      {
        id: 'call-002',
        phone: '(555) 234-7890',
        agent: 'AI Agent Nova',
        status: 'active',
        duration: 94,
        startedAt: new Date(Date.now() - 94000).toISOString(),
        callSid: 'CA1a2b3c4d5e6f7890',
        sentiment: 'neutral',
        transcript: [
          { speaker: 'ai', text: 'Good afternoon, Dewberry Resort. This is Nova. How may I assist you?', time: '0:00' },
          { speaker: 'caller', text: 'I have a reservation and I need to change the dates.', time: '0:06' },
          { speaker: 'ai', text: 'Of course, I can help with that. Could you give me your reservation confirmation number?', time: '0:12' },
          { speaker: 'caller', text: 'It\'s RES-4482-BK.', time: '0:20' }
        ]
      },
      {
        id: 'call-003',
        phone: '(555) 876-5432',
        agent: 'AI Agent Luna',
        status: 'queued',
        duration: 42,
        startedAt: new Date(Date.now() - 42000).toISOString(),
        callSid: 'CA0f1e2d3c4b5a6978',
        sentiment: 'neutral',
        transcript: []
      },
      {
        id: 'call-004',
        phone: '(555) 111-2233',
        agent: 'AI Agent Orion',
        status: 'active',
        duration: 312,
        startedAt: new Date(Date.now() - 312000).toISOString(),
        callSid: 'CA7890abcdef123456',
        sentiment: 'negative',
        transcript: [
          { speaker: 'ai', text: 'Thank you for your patience. I understand your concern about the billing issue.', time: '0:00' },
          { speaker: 'caller', text: 'I was charged twice for my stay last month and nobody has resolved it yet.', time: '0:08' },
          { speaker: 'ai', text: 'I sincerely apologize for the inconvenience. Let me pull up your account and look into this right away. Can you confirm the email on file?', time: '0:16' },
          { speaker: 'caller', text: 'Yes, it\'s john.martinez@email.com.', time: '0:28' },
          { speaker: 'ai', text: 'Thank you, Mr. Martinez. I can see the duplicate charge. I\'m initiating a refund of $347.00 to your card ending in 4821. You should see it within 3-5 business days.', time: '0:35' }
        ]
      },
      {
        id: 'call-005',
        phone: '(555) 444-5566',
        agent: 'AI Agent Nova',
        status: 'hold',
        duration: 156,
        startedAt: new Date(Date.now() - 156000).toISOString(),
        callSid: 'CAfedcba9876543210',
        sentiment: 'positive',
        transcript: [
          { speaker: 'ai', text: 'Welcome to Dewberry Resort! How can I make your day better?', time: '0:00' },
          { speaker: 'caller', text: 'I\'m looking to book the spa package for my wife\'s birthday.', time: '0:07' },
          { speaker: 'ai', text: 'That sounds wonderful! We have three birthday spa packages. Let me place you on a brief hold while I check today\'s availability with our spa team.', time: '0:14' }
        ]
      },
      {
        id: 'call-006',
        phone: '(555) 777-8899',
        agent: 'AI Agent Luna',
        status: 'active',
        duration: 28,
        startedAt: new Date(Date.now() - 28000).toISOString(),
        callSid: 'CA112233445566778899',
        sentiment: 'positive',
        transcript: [
          { speaker: 'ai', text: 'Thank you for calling Dewberry Resort. How can I help?', time: '0:00' },
          { speaker: 'caller', text: 'What time is checkout?', time: '0:05' }
        ]
      }
    ];
  }

  connectedCallback() {
    super.connectedCallback();
    // Live duration counter
    this._timerInterval = setInterval(() => {
      this.calls = this.calls.map(call => ({
        ...call,
        duration: call.duration + 1
      }));
    }, 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._timerInterval) {
      clearInterval(this._timerInterval);
      this._timerInterval = null;
    }
  }

  get _filteredCalls() {
    let filtered = this.calls;

    // Apply status filter
    if (this._filter !== 'all') {
      filtered = filtered.filter(c => c.status === this._filter);
    }

    // Apply search
    if (this._searchQuery.trim()) {
      const q = this._searchQuery.trim().toLowerCase();
      filtered = filtered.filter(c =>
        c.phone.toLowerCase().includes(q) ||
        c.agent.toLowerCase().includes(q) ||
        c.callSid.toLowerCase().includes(q)
      );
    }

    return filtered;
  }

  get _selectedCall() {
    if (!this._selectedCallId) return null;
    return this.calls.find(c => c.id === this._selectedCallId) || null;
  }

  get _statusCounts() {
    const counts = { all: this.calls.length, active: 0, queued: 0, hold: 0 };
    for (const call of this.calls) {
      if (counts[call.status] !== undefined) counts[call.status]++;
    }
    return counts;
  }

  render() {
    const counts = this._statusCounts;

    return html`
      ${iconStyles}
      <div class="monitor-layout">
        <!-- Left Panel -->
        <div class="left-panel">
          <div class="left-header">
            <div class="left-title">Live Calls</div>

            <!-- Filter Tabs -->
            <div class="filter-tabs">
              ${this._renderFilterTab('all', 'All', counts.all)}
              ${this._renderFilterTab('active', 'Active', counts.active)}
              ${this._renderFilterTab('queued', 'Queued', counts.queued)}
              ${this._renderFilterTab('hold', 'Hold', counts.hold)}
            </div>

            <!-- Search -->
            <div class="search-box">
              <i class="fa-sharp fa-light fa-magnifying-glass search-icon"></i>
              <input
                type="text"
                placeholder="Search by phone number..."
                .value=${this._searchQuery}
                @input=${(e) => { this._searchQuery = e.target.value; }}
              />
            </div>
          </div>

          <!-- Call List -->
          <div class="call-list">
            ${this._filteredCalls.length > 0
              ? this._filteredCalls.map(call => this._renderCallCard(call))
              : html`
                <div class="empty-panel" style="padding: var(--space-8); height: auto;">
                  <div class="empty-icon" style="width: 48px; height: 48px; font-size: 1.2rem;">
                    <i class="fa-sharp fa-light fa-phone-slash"></i>
                  </div>
                  <div class="empty-title" style="font-size: var(--text-base);">No Calls Found</div>
                  <div class="empty-desc" style="font-size: var(--text-xs);">
                    ${this._searchQuery ? 'No calls match your search' : 'No calls in this category'}
                  </div>
                </div>
              `
            }
          </div>
        </div>

        <!-- Right Panel -->
        <div class="right-panel">
          ${this._selectedCall
            ? this._renderCallDetail(this._selectedCall)
            : this._renderEmptyDetail()
          }
        </div>
      </div>
    `;
  }

  _renderFilterTab(value, label, count) {
    const isActive = this._filter === value;
    return html`
      <button
        class="filter-tab ${isActive ? 'active' : ''}"
        @click=${() => { this._filter = value; }}
      >
        ${label}<span class="tab-count">${count}</span>
      </button>
    `;
  }

  _renderCallCard(call) {
    const isSelected = this._selectedCallId === call.id;

    return html`
      <div
        class="call-card ${isSelected ? 'selected' : ''}"
        @click=${() => { this._selectedCallId = call.id; }}
      >
        <div class="call-card-top">
          <span class="call-card-phone">${call.phone}</span>
          <span class="call-card-badge ${call.status}">
            <span class="badge-dot"></span>
            ${call.status === 'active' ? 'Active' : call.status === 'queued' ? 'Queued' : 'Hold'}
          </span>
        </div>
        <div class="call-card-meta">
          <span class="call-card-meta-item">
            <i class="fa-sharp fa-light fa-user"></i>
            ${call.agent}
          </span>
          <span class="call-card-meta-item call-card-duration">
            <i class="fa-sharp fa-light fa-timer"></i>
            ${this._formatDuration(call.duration)}
          </span>
          <span class="call-card-sentiment ${call.sentiment}" title="${call.sentiment} sentiment"></span>
        </div>
      </div>
    `;
  }

  _renderCallDetail(call) {
    return html`
      <!-- Detail Header -->
      <div class="detail-header">
        <div class="detail-header-top">
          <span class="detail-phone">${call.phone}</span>
          <span class="detail-live-badge">
            <span class="detail-live-dot"></span>
            Live
          </span>
        </div>
        <div class="detail-meta">
          <span class="detail-meta-item">
            <i class="fa-sharp fa-light fa-user"></i>
            <span class="detail-meta-value">${call.agent}</span>
          </span>
          <span class="detail-meta-item">
            <i class="fa-sharp fa-light fa-fingerprint"></i>
            <span class="detail-meta-value mono">${call.callSid}</span>
          </span>
          <span class="detail-meta-item">
            <i class="fa-sharp fa-light fa-clock"></i>
            <span class="detail-meta-value">Started ${this._formatTime(call.startedAt)}</span>
          </span>
          <span class="detail-meta-item">
            <i class="fa-sharp fa-light fa-timer"></i>
            <span class="detail-meta-value mono">${this._formatDuration(call.duration)}</span>
          </span>
        </div>
      </div>

      <!-- Transcript -->
      <div class="transcript-area">
        ${call.transcript && call.transcript.length > 0
          ? call.transcript.map(msg => html`
            <div class="transcript-message ${msg.speaker === 'ai' ? 'ai' : 'caller'}">
              <span class="transcript-label">
                ${msg.speaker === 'ai'
                  ? html`<i class="fa-sharp fa-light fa-robot"></i> AI Agent`
                  : html`<i class="fa-sharp fa-light fa-user"></i> Caller`
                }
              </span>
              <div class="transcript-bubble">${msg.text}</div>
              <span class="transcript-time">${msg.time}</span>
            </div>
          `)
          : html`
            <div class="empty-panel" style="height: auto; padding: var(--space-12);">
              <div class="empty-icon" style="width: 56px; height: 56px; font-size: 1.5rem;">
                <i class="fa-sharp fa-light fa-message-dots"></i>
              </div>
              <div class="empty-title" style="font-size: var(--text-base);">Waiting for Conversation</div>
              <div class="empty-desc">Transcript will appear here once the call connects</div>
            </div>
          `
        }
      </div>

      <!-- Actions Bar -->
      <div class="actions-bar">
        <button class="action-btn primary">
          <i class="fa-sharp fa-light fa-headset"></i>
          Listen Live
        </button>
        <button class="action-btn">
          <i class="fa-sharp fa-light fa-ear-listen"></i>
          Whisper
        </button>
        <button class="action-btn">
          <i class="fa-sharp fa-light fa-arrow-right-arrow-left"></i>
          Transfer
        </button>
        <div class="actions-spacer"></div>
        <button class="action-btn danger">
          <i class="fa-sharp fa-light fa-flag"></i>
          Flag
        </button>
        <button class="action-btn">
          <i class="fa-sharp fa-light fa-note-sticky"></i>
          Notes
        </button>
      </div>
    `;
  }

  _renderEmptyDetail() {
    return html`
      <div class="empty-panel">
        <div class="empty-icon">
          <i class="fa-sharp fa-light fa-phone-arrow-up-right"></i>
        </div>
        <div class="empty-title">Select a call to view details</div>
        <div class="empty-desc">
          Choose a call from the list on the left to see the live transcript, caller information, and available actions.
        </div>
      </div>
    `;
  }

  _formatDuration(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  _formatTime(isoString) {
    if (!isoString) return '--:--';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }
}

customElements.define('live-calls-monitor', LiveCallsMonitor);
